import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Users, Loader2, AlertCircle, RefreshCw, Circle } from "lucide-react";

/**
 * v23.23 — Managed driver directory. Shows every Raanah team driver with
 * today's shift status (on_shift · worked_today · off_today), hours worked
 * today, and last clock-out time. Auto-refreshes every 60s.
 */
function fmtDurationH(secs) {
  if (!secs && secs !== 0) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

const STATUS_META = {
  on_shift: { label: "On shift", dot: "bg-[#5BB247]", pillCls: "text-[#2c8642] bg-[#5BB247]/10 border-[#5BB247]/40" },
  worked_today: { label: "Worked today", dot: "bg-[#F5A623]", pillCls: "text-[#A66800] bg-[#F5A623]/10 border-[#F5A623]/40" },
  off_today: { label: "Off today", dot: "bg-[#0A0A0A]/30", pillCls: "text-[#475569] bg-white border-[#0A0A0A]/15" },
};

export default function AdminManagedDirectoryPanel() {
  const [rows, setRows] = React.useState([]);
  const [counts, setCounts] = React.useState({ on_shift: 0, worked_today: 0, off_today: 0, total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState("");
  const [filter, setFilter] = React.useState("all"); // all | on_shift | worked_today | off_today

  const load = React.useCallback(async (isBackground = false) => {
    if (isBackground) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/managed-directory");
      setRows(data.drivers || []);
      setCounts(data.counts || { on_shift: 0, worked_today: 0, off_today: 0, total: 0 });
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load managed drivers");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    const t = setInterval(() => load(true), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <section className="mb-12" data-testid="admin-managed-directory-panel">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <Users size={20} /> Managed driver directory
          </h2>
          <p className="mt-1 text-sm text-[#475569]">
            Your Raanah team, with today&apos;s shift status. Refreshes every 60s.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="admin-managed-directory-refresh"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-bold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-60"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Managed status filter">
        {[
          { key: "all", label: `All (${counts.total})` },
          { key: "on_shift", label: `On shift · ${counts.on_shift}` },
          { key: "worked_today", label: `Worked today · ${counts.worked_today}` },
          { key: "off_today", label: `Off today · ${counts.off_today}` },
        ].map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={filter === t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`managed-directory-filter-${t.key}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.14em] font-bold border transition-colors ${
              filter === t.key
                ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:bg-[#F5F5F5]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="admin-managed-directory-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="text-[#475569] text-sm inline-flex items-center gap-2" data-testid="admin-managed-directory-loading">
          <Loader2 size={14} className="animate-spin" /> Loading team…
        </div>
      ) : shown.length === 0 ? (
        <div className="bg-white border border-[#0A0A0A]/10 border-dashed p-8 text-center text-sm text-[#475569]" data-testid="admin-managed-directory-empty">
          {counts.total === 0
            ? "No managed drivers yet. Managed drivers register from the sign-up page (choose Managed by Raanah)."
            : "No drivers match this filter right now."}
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]/10" data-testid="admin-managed-directory-list">
          {shown.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.off_today;
            return (
              <div
                key={r.id}
                data-testid={`managed-row-${r.id}`}
                className="px-5 py-4 border-b last:border-b-0 border-[#0A0A0A]/10 flex items-center gap-4 flex-wrap"
              >
                <div className="w-9 h-9 bg-[#1E4D55] text-white flex items-center justify-center font-display font-bold text-xs shrink-0">
                  {(r.name || r.email).slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-[#0A0A0A] text-sm truncate">{r.name || r.email}</p>
                  <p className="text-xs text-[#475569] truncate">
                    {r.email}{r.phone ? ` · ${r.phone}` : ""}
                  </p>
                </div>
                <span
                  data-testid={`managed-status-${r.id}`}
                  className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold border px-2 py-1 ${meta.pillCls}`}
                >
                  <Circle size={8} className={`${meta.dot} rounded-full border-0`} fill="currentColor" />
                  {meta.label}
                </span>
                <div className="text-right min-w-[90px]">
                  <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Today</p>
                  <p className="text-sm font-semibold text-[#0A0A0A]" data-testid={`managed-hours-${r.id}`}>
                    {fmtDurationH(r.today_seconds)}
                  </p>
                </div>
                <div className="text-right min-w-[110px] hidden sm:block">
                  <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">
                    {r.status === "on_shift" ? "Since" : r.last_ended_at ? "Last out" : "—"}
                  </p>
                  <p className="text-xs text-[#0A0A0A]">
                    {r.status === "on_shift" && r.current_shift_started_at
                      ? new Date(r.current_shift_started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                      : r.last_ended_at
                      ? new Date(r.last_ended_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
